import React from 'react'
import { useFormikContext } from 'formik'
import { Select, MenuItem, Typography } from '@mui/material'
import { SectionTitle } from '@/components/FilterBottomSheet/components/SectionTitle'
import { ChipRow } from '@/components/FilterBottomSheet/components/ChipRow'
import { DotLabel } from '@/components/FilterBottomSheet/components/DotLabel'
import type { FormState, FilterBottomSheetProps } from '@/components/FilterBottomSheet/types'
import { STATUS_CONFIG as CHARGING_STATUS_CONFIG } from '@/modules/Wallet/parts/HistoryItem'
import { ChargingSessionInAppNotificationStatus } from '@/lib/in-app-notifications/categories/charging_session'
import { FilterBottomSheet } from '@/components/FilterBottomSheet'
import { buildFormState as baseBuildFormState, buildAppliedFilters as baseBuildAppliedFilters, type ConverterConfig } from '@/components/FilterBottomSheet/helpers/converters'
import { PeriodFilter } from '@/components/FilterBottomSheet/components/PeriodFilter'

const CONFIG: ConverterConfig = {
  dateKeys: {
    from: 'startTimeFrom',
    to: 'startTimeTo',
  },
}

const buildFormState = (filters: Record<string, unknown>) =>
  baseBuildFormState(filters, CONFIG)

const buildAppliedFilters = (form: Parameters<typeof baseBuildAppliedFilters>[0]) =>
  baseBuildAppliedFilters(form, CONFIG)

const CHARGING_STATUSES = (['IN_PROGRESS', 'FINISHED', 'FAILED', 'FAILED_PAYMENT'] as ChargingSessionInAppNotificationStatus[]).map((value) => ({
  value,
  label: CHARGING_STATUS_CONFIG[value].label,
}))

const ALL_STATIONS_LABEL = 'Todos'

export const StatusChips: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<FormState>()
  return (
    <>
      <SectionTitle>Status</SectionTitle>
      <ChipRow
        options={CHARGING_STATUSES}
        selected={values.status}
        onChange={(status) => setFieldValue('status', status)}
      />
    </>
  )
}

export const StationSelector: React.FC<{ stations: string[] }> = ({ stations }) => {
  const { values, setFieldValue } = useFormikContext<FormState>()
  const station = stations.includes(values.stationName) ? values.stationName : ''

  return (
    <>
      <SectionTitle>Estação</SectionTitle>
      <Select
        value={station}
        onChange={(e) => setFieldValue('stationName', e.target.value)}
        fullWidth
        size="small"
        displayEmpty
        sx={{ mb: 2 }}
        renderValue={(value) => (
          <Typography variant="body2" sx={value ? undefined : { color: 'text.secondary' }}>
            {value || ALL_STATIONS_LABEL}
          </Typography>
        )}
      >
        {['', ...stations].map((name) => (
          <MenuItem key={name} value={name}>
            <DotLabel label={name || ALL_STATIONS_LABEL} active={name === station} muted={!name} />
          </MenuItem>
        ))}
      </Select>
    </>
  )
}

type ChargingFilterBottomSheetProps = Omit<FilterBottomSheetProps, 'type' | 'buildFormState' | 'buildAppliedFilters'> & {
  stations?: string[]
}

export const ChargingFilterBottomSheet: React.FC<ChargingFilterBottomSheetProps> = ({
  filters,
  onApply,
  onClear,
  isOpen,
  onClose,
  stations = [],
}) => (
  <FilterBottomSheet
    filters={filters}
    onApply={onApply}
    onClear={onClear}
    isOpen={isOpen}
    onClose={onClose}
    buildFormState={buildFormState}
    buildAppliedFilters={buildAppliedFilters}
  >
    <StatusChips />
    <StationSelector stations={stations} />
    <PeriodFilter />
  </FilterBottomSheet>
)
